import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Shield, Star, MessageCircle } from "lucide-react";
import { playBigCheers } from "@/lib/sfx";
import { playVoice } from "@/lib/tts";
import { shareTrophy } from "@/lib/share";

const COLORS = ["#FFC83D", "#FF6B4A", "#22C08A", "#3D8BFF", "#A66CFF", "#EC4899"];

export default function ChapterCelebration({ name, childId, chapter, lang = "en", onClose }) {
  const [open, setOpen] = useState(true);
  const fr = lang === "fr";
  useEffect(() => {
    playBigCheers();
    if (localStorage.getItem("aa_voice_on") !== "off") {
      const t = setTimeout(() => playVoice(fr ? `Bravo ${name} ! Tu as terminé tout le chapitre ! Tu es un champion !` : `Congratulations ${name}! You finished the whole chapter! You are a champion!`, lang), 1600);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => { setOpen(false); onClose?.(); };
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-[#1E5EFF] flex items-center justify-center p-5 overflow-hidden" data-testid="chapter-celebration">
          {[...Array(60)].map((_, k) => (
            <motion.div key={k} className="absolute rounded-sm" style={{ left: `${(k * 17) % 100}%`, top: "-4%", width: 10 + (k % 4) * 3, height: 14 + (k % 3) * 4, background: COLORS[k % COLORS.length] }}
              initial={{ y: 0, rotate: 0, opacity: 0 }} animate={{ y: "112vh", rotate: 540, opacity: [0, 1, 1, 0.7] }}
              transition={{ duration: 2.4 + (k % 5) * 0.35, delay: (k % 12) * 0.15, repeat: Infinity }} />
          ))}
          <motion.div initial={{ scale: 0.4, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 220, damping: 12 }}
            className="relative bg-white rounded-[40px] p-8 sm:p-10 max-w-md w-full text-center font-kids shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
            <motion.div animate={{ y: [0, -10, 0], rotate: [0, -6, 6, 0] }} transition={{ duration: 1.2, repeat: Infinity }}
              className="w-32 h-32 mx-auto rounded-full bg-[#FFC83D] border-4 border-slate-900 flex items-center justify-center shadow-[5px_5px_0_0_#0F172A]">
              <Trophy size={68} className="text-slate-900" />
            </motion.div>
            <motion.h2 initial={{ scale: 0 }} animate={{ scale: [0, 1.25, 1] }} transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl sm:text-5xl font-black mt-6 text-slate-900" data-testid="chapter-celebration-title">
              {fr ? `Bravo ${name} !` : `Well done ${name}!`}
            </motion.h2>
            <p className="text-xl font-bold mt-3 text-[#7C3AED]">{fr ? "Chapitre terminé !" : "Chapter complete!"}</p>
            {chapter && <p className="font-parent text-slate-600 mt-1" data-testid="chapter-celebration-chapter">{chapter}</p>}
            <div className="flex justify-center gap-2 mt-4">
              {[0, 1, 2].map((s) => (
                <motion.div key={s} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6 + s * 0.2, type: "spring", stiffness: 300 }}>
                  <Star size={44} fill="#FACC15" className="text-yellow-400" />
                </motion.div>
              ))}
            </div>
            <p className="inline-flex items-center gap-2 mt-4 bg-blue-50 border-2 border-blue-600 rounded-full px-4 py-1.5 font-parent font-semibold text-blue-700">
              <Shield size={18} /> {fr ? "Nouveau bouclier gagné !" : "New shield won!"}
            </p>
            <button onClick={close} data-testid="chapter-celebration-close"
              className="mt-6 w-full h-14 rounded-full bg-[#22C08A] text-white text-xl font-black border-4 border-slate-900 shadow-[4px_4px_0_0_#0F172A] active:translate-y-0.5">
              {fr ? "Youpi !" : "Hooray!"}
            </button>
            {childId && (
              <button onClick={() => shareTrophy(childId, lang).catch(() => {})} data-testid="chapter-celebration-share"
                className="mt-3 w-full h-12 rounded-full bg-[#25D366] text-white font-bold border-4 border-slate-900 inline-flex items-center justify-center gap-2">
                <MessageCircle size={20} /> {fr ? "Partager sur WhatsApp" : "Share on WhatsApp"}
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
